import React, { useEffect, useState } from 'react';
import { Card, Skeleton, Alert, Empty, Typography, Button } from 'antd';
import { SafetyOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useRichTextStyles } from '../utils/useRichTextStyles'; // Add this import

const { Title, Paragraph } = Typography;

const Privacy = () => {
  useRichTextStyles(); // Add this hook
  
  const [privacy, setPrivacy] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchPrivacy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching privacy policy...');
      
      // Create a clean axios instance
      const publicAxios = axios.create({
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const response = await publicAxios.get('/api/privacy');
      console.log('✅ Privacy policy fetched successfully:', response.data);
      
      setPrivacy(response.data.data.privacy);
      setLastUpdated(response.data.data.lastUpdated);
    } catch (err) {
      console.error('❌ Error fetching privacy policy:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to load privacy policy');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrivacy();
  }, []);

  if (loading) {
    return (
      <div className='space-y-6'>
        <Card className='shadow-soft border border-border-light rounded-xl'>
          <div className='flex items-center space-x-3 mb-6'>
            <div className='w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full flex items-center justify-center'>
              <SafetyOutlined className='text-xl text-white' />
            </div>
            <div>
              <Title level={2} className='mb-1'>
                Privacy Policy
              </Title>
              <Paragraph className='text-gray-600 mb-0'>
                Our commitment to protecting your privacy
              </Paragraph>
            </div>
          </div>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className='space-y-6'>
        <Card className='shadow-soft border border-border-light rounded-xl'>
          <Alert
            message='Error Loading Privacy Policy'
            description={`There was an error loading the privacy policy: ${error}. Please try again.`}
            type='error'
            showIcon
            action={
              <Button
                onClick={fetchPrivacy}
                icon={<ReloadOutlined />}
                type="primary"
              >
                Retry
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <Card className='shadow-soft border border-border-light rounded-xl'>
        <div className='flex items-center space-x-3 mb-4'>
          <div className='w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full flex items-center justify-center'>
            <SafetyOutlined className='text-xl text-white' />
          </div>
          <div>
            <Title level={2} className='mb-1'>
              Privacy Policy
            </Title>
            <Paragraph className='text-gray-600 mb-0'>
              Our commitment to protecting your privacy
            </Paragraph>
          </div>
        </div>

        {lastUpdated && (
          <Paragraph className='text-sm text-gray-500 mb-0'>
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </Paragraph>
        )}
      </Card>

      {/* Content */}
      <Card className='shadow-soft border border-border-light pl-10 py-10 pr-80 rounded-xl'>
        {privacy ? (
          <div
            className='rich-text-content prose prose-lg max-w-none' // Add rich-text-content class
            dangerouslySetInnerHTML={{ __html: privacy }}
            style={{
              lineHeight: '1.7',
              fontSize: '16px',
              fontFamily: 'Montserrat, sans-serif',
            }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <p>No privacy policy available</p>
                <p className='text-sm text-gray-500'>
                  Privacy policy content will appear here once it's been
                  created.
                </p>
              </div>
            }
          />
        )}
      </Card>
    </div>
  );
};

export default Privacy;